<template>
  <article :class="wrapperClasses">
    <div class="time">{{ formattedTime }}</div>

    <div class="feed-item-content">
      <div class="author">{{ author }}</div>

      <div
        v-if="action"
        :class="['action', eventTypeName]"
      >
        {{ action }}
      </div>

      <div v-if="entityType" class="entity-type">
        {{ entityType }}
      </div>

      <!-- Позиция заказа, созданная из предзаказа: номер — ссылка на карточку позиции -->
      <div v-if="isOrderPositionFromPreorder" class="entity-id">
        <a
          v-if="eventTypeName !== 'delete'"
          href="#"
          :title="`Открыть заказ №${orderPositionNumber} &quot;${entityIdentifier}&quot;`"
          @click.prevent="handleEntityIdentifierClick()"
        >
          №{{ orderPositionNumber }}
        </a>
        <span v-else>№{{ orderPositionNumber }}</span>
      </div>

      <div v-if="entityIdentifier" class="entity-name">
        <a
          v-if="isEntityClickable"
          href="#"
          :title="`Скачать ${entityIdentifier}`"
          @click.prevent="handleEntityIdentifierClick()"
        >
          {{ entityIdentifier }}
        </a>
        <span v-else>"{{ entityIdentifier }}"</span>
      </div>

      <!-- В производственных планах требуется выводить количество -->
      <div v-if="showProductionPlanProductQty" class="entity-qty">
        <span>Количество: {{ payload.data.qty }} шт.</span>
      </div>

      <div class="changes">
        <div class="description">

          <div
            v-for="(change, idx) in changeRecords"
            :key="idx"
            v-html="change"
          />

          <div v-if="reasonText">
            Причина изменения: {{ reasonText }}
          </div>
        </div>
      </div>
    </div>
  </article>
</template>

<script lang="ts">

import FeedRecord        from '@/core/services/Feeds/FeedRecord';
import ChangelogEntry    from '@/core/services/Changelogs/ChangelogEntry';
import ChangelogsService from '@/core/services/Changelogs/ChangelogsService';
import FilesService      from '@/core/services/Files/FilesService';

export default defineComponent({
  name: 'FeedItemChangelog',

  props: {
    record: {
      type:     Object as PropType<FeedRecord>,
      required: true,
    },
  },

  computed: {
    payload(): ChangelogEntry {
      return this.record.payload as ChangelogEntry;
    },

    // Тип события для css-класса action ('create' | 'update' | 'delete')
    eventTypeName(): string {
      return this.payload.eventTypeName;
    },

    reasonText(): string | null {
      return this.payload.reasonText ?? null;
    },

    // ===== Производный текст =====

    author(): string {
      // В логах производственных планов надо показывать автора
      if (this.isProductionPlanProduct) {
        return this.payload.author?.name ?? this.payload.partnerName ?? 'Робот автоматически';
      }

      // Запись нашей организации — показываем сотрудника (нам важно, кто из своих).
      // Чужая — имя организации/маску из partnerName (кто именно из чужих — не наше дело).
      if (this.record.isMine) {
        return this.$core.currentPartner.name;
        // 24.08.2026. Публичный фид скрывает все имена. Даже если запись собственная
        // return this.payload.author?.name ?? 'Робот автоматически';
      }
      else if (this.payload.partnerName) {
        return this.payload.partnerName;
      }
      else {
        return this.payload.author?.name ?? 'Робот автоматически';
      }
    },

    action(): string | undefined {
      // Для ЗТК (позиции транспортного заказа) — особый глагол при создании
      if (this.isTransportOrderPosition && this.eventTypeName === 'create') {
        return 'добавил(а)';
      }
      else {
        return ChangelogsService.EVENT_TYPE_ACTION_LABELS[this.eventTypeName];
      }
    },

    entityType(): string | null {
      // Файл — отдельный человекочитаемый тип
      if (this.isFile) {
        return 'файл';
      }

      // Для истории товара в производственных планах — месяц и год плана,
      // чтобы различить одинаковые товары в общем списке
      if (this.isProductionPlanProduct && this.payload.data?.name) {
        const date  = new Date(this.payload.data.arrivalDate);
        const month = this.$core.utils.date.getMonthName(date.getMonth(), 'long').toUpperCase();
        const year  = date.getFullYear();
        return `товар (план ${month} ${year})`;
      }

      if (this.payload.data?.seller) {
        return this.payload.data.seller.name;
      }

      const label = this.payload.entityTypeName;
      if (typeof label === 'string') {
        return label.toLowerCase();
      }
      else {
        return null;
      }
    },

    entityIdentifier(): string | null {
      // Файл — показываем имя сущности
      if (this.isFile) {
        return this.payload.parentEntity.name ?? null;
      }

      // Для productionPlan имя продукта уже выводится через entityType
      if (this.isProductionPlanProduct) {
        return `${this.payload.data.code} ${this.payload.data.name}`;
      }

      // Приоритет: name > seller.name > отформатированный id
      if (this.payload.parentEntity.name) {
        return this.payload.parentEntity.name;
      }

      if (this.record.parentEntity.typeName === 'tenderParticipation') {
        return this.payload.data?.seller?.name ?? null;
      }

      if (['productionPlan', 'partner'].includes(this.record.parentEntity.typeName)) {
        return null;
      }

      return this.$core.utils.string.formatWithDelimiter(this.payload.parentEntity.id);
    },

    // Номер позиции заказа с разделителями — текст ссылки и подсказка
    orderPositionNumber(): string {
      return this.$core.utils.string.formatWithDelimiter(this.payload.parentEntity.id);
    },

    changeRecords(): string[] {
      return this.payload.changeRecords;
    },

    // ===== Проверки =====

    isFile(): boolean {
      return this.payload.parentEntity.typeName === 'file';
    },

    isTransportOrderPosition(): boolean {
      return this.record.parentEntity.typeName === 'transportOrderPosition';
    },

    isProductionPlanProduct(): boolean {
      return this.payload.parentEntity.typeName === 'productionPlanProduct';
    },

    // Событие по позиции заказа в фиде позиции предзаказа — заказ, созданный на основе предзаказа
    isOrderPositionFromPreorder(): boolean {
      return this.record.parentEntity.typeName === 'preorderPosition'
        && this.payload.parentEntity.typeName === 'orderPosition';
    },

    showProductionPlanProductQty(): boolean {
      return this.payload.data?.qty;
    },

    // Идентификатор кликабелен только для существующего файла (не для удаления).
    // Позиция заказа из предзаказа кликабельна по номеру, а не по названию товара
    isEntityClickable(): boolean {
      return this.isFile && this.eventTypeName !== 'delete';
    },

    // ===== Классы / время =====

    wrapperClasses(): string[] {
      const classes = ['feed-item', 'event', 'changelog'];

      // запись нашей организации — подсвечиваем
      if (this.record.isMine) {
        classes.push('is-user');
      }

      // только что появившаяся запись — блинк (дельта-обновление ленты)
      if (this.record.isNew) {
        classes.push('is-show');
      }

      return classes;
    },

    formattedTime(): string {
      return this.record.time.format('HH|mm');
    },
  },

  methods: {
    // Клик по идентификатору: файл — скачивание, номер позиции заказа из предзаказа — открытие карточки.
    // Для прочих сущностей идентификатор не кликабелен.
    async handleEntityIdentifierClick(): Promise<void> {
      if (this.isFile === true) {
        try {
          const descriptor = await FilesService.get(
            this.payload.parentEntity.id as string,
            { silent: true },
          );
          void FilesService.downloadFile(descriptor);
        } catch {
          this.$core.showNotification.error('Ошибка при скачивании файла.');
          this.$core.showNotification.info('Возможно, файл был удалён, проверьте список файлов.');
        }
      }
      else if (this.isOrderPositionFromPreorder === true) {
        // id в указателе приходит с бэка строкой, проп карточки — Number
        void this.$core.dialogs.OrderPositionCard.showAsPromise({
          id: Number(this.payload.parentEntity.id),
        });
      }
      else {
        // прочие сущности не кликабельны
      }
    },
  },
});
</script>
